import { FC, useMemo, useState } from 'react';
import { environment } from '../../../../@xmcloud/core/messages/en';
import { pageSize } from '../../../../@xmcloud/utils/constants';
import { deployStatusUpdateInterval } from '../../../config/envConfig';
import { useGetEnvironmentDeploymentList } from '../../../services/environments';
import {
	AlertStatus,
	LoadingSkeleton,
	RenderPlaceholder,
	Table,
} from '../../../shared-components';
import {
	useDeploymentsTableColumns,
	TDeployments,
} from '../columns/deployments';
import CancelDeployment from '../deployment-logs/CancelDeployment';
import DeployBuildOtherEnv from '../components/DeployBuildOtherEnv';
import { status } from '../deployment-logs/handlers';
import { ColumnDef } from '@tanstack/react-table';
import {
	useEnvironmentDetails,
	useProjectDetails,
} from '../../../../@xmcloud/hooks';
import { getReadableDate } from '../../../../@xmcloud/utils/dateUtils';
import { compareAsc } from 'date-fns';
import { useGetEnvType } from '../hooks/useGetEnvType';

const { deployment: deploymenttxt, repositoryChanged: repositoryChangedtxt } =
	environment;

const headers = ['name', 'Latest deployment', 'Status', ''];

const DeploymentsTable: FC = () => {
	const { isLegacyEnvsRoute, projectId, environmentId } = useGetEnvType();
	const [showCancelDeploymentModal, setshowCancelDeploymentModal] =
		useState(false);
	const [deploymentId, setDeploymentId] = useState('');
	const [deployToOtherEnvModal, setDeployToOtherEnvModal] = useState(false);

	const {
		isLoading: isProjectDetailsLoading,
		projectName,
		repositoryLinkedAt: projectRepositoryLinkedAt,
		project,
	} = useProjectDetails(projectId!);
	const { environmentName, repositoryLinkedAt: envRepositoryLinkedAt } =
		useEnvironmentDetails(environmentId!);

	const { data, isLoading: isDeploymentsLoading } =
		useGetEnvironmentDeploymentList({
			environmentId: environmentId!,
			projectId: projectId!,
			refetchInterval: parseInt(deployStatusUpdateInterval, 10),
			refetchIntervalInBackground: true,
		});

	const deployments = useMemo(() => {
		const allDeployments = data?.data || [];

		return allDeployments.map((d) => ({
			...d,
			status: Object.values(status)[d.calculatedStatus],
		}));
	}, [data?.data]);

	const columns: ColumnDef<TDeployments, any>[] = useDeploymentsTableColumns(
		setDeploymentId,
		setDeployToOtherEnvModal,
		setshowCancelDeploymentModal,
		project?.isLocked,
	);
	const isLoading = isProjectDetailsLoading || isDeploymentsLoading;

	if (isLoading) return <LoadingSkeleton amount={pageSize + 1} />;
	if (deployments.length === 0)
		return <RenderPlaceholder item={deploymenttxt.toLocaleLowerCase()} />;

	const lastDeployment = deployments[deployments.length - 1];
	const lastDeploymentDate = lastDeployment?.createdAt;

	// project repository linked at date
	const lastProjectRepoChangeDate =
		projectRepositoryLinkedAt &&
		getReadableDate(projectRepositoryLinkedAt, 'PPp');

	// environment repository linked at date
	const lastEnvRepoChangeDate =
		envRepositoryLinkedAt && getReadableDate(envRepositoryLinkedAt, 'PPp');

	// compare the last repository change date for project with the last deployment date
	const projectDateResult = compareAsc(
		projectRepositoryLinkedAt,
		lastDeploymentDate,
	);

	// compare the last repository change date for environment with the last deployment date
	const envDateResult = compareAsc(envRepositoryLinkedAt, lastDeploymentDate);

	//if result is greater than 0, repository changed after last deployment
	const showInfoAlert = isLegacyEnvsRoute
		? projectDateResult > 0
		: envDateResult > 0;

	const fieldName = isLegacyEnvsRoute ? projectName : environmentName;

	// get the last repository change date
	const lastRepoChangDate = isLegacyEnvsRoute
		? lastProjectRepoChangeDate
		: lastEnvRepoChangeDate;

	return (
		<>
			{!!showInfoAlert && (
				<AlertStatus
					description={repositoryChangedtxt}
					status="info"
					data-testid="repo-changed-info-alert"
					boldText={fieldName}
					variableTxt2={lastRepoChangDate}
					mb={4}
				/>
			)}
			<Table
				{...{
					columns,
					tableData: deployments,
					dataTestId: 'deployment-table',
					initialSort: [{ id: 'createdAt', desc: true }],
					showPagination: deployments.length > pageSize,
					headers,
				}}
			/>
			{showCancelDeploymentModal && (
				<CancelDeployment
					environmentId={environmentId!}
					deploymentId={deploymentId}
					openModal={showCancelDeploymentModal}
					setOpenModal={setshowCancelDeploymentModal}
				/>
			)}
			{deployToOtherEnvModal && (
				<DeployBuildOtherEnv
					openModal={deployToOtherEnvModal}
					setOpenModal={setDeployToOtherEnvModal}
					deploymentId={deploymentId}
					environmentId={environmentId!}
					projectId={projectId!}
				/>
			)}
		</>
	);
};

export default DeploymentsTable;
